import type { StateCreator } from 'zustand';
import type {
  StrategyAccount, FeedbackEntry, ProfileKit, StrategyPerformance,
  ProfileKitV2, KitPreset, KitGenerationLog, KitCounts, ProfileType,
  BrandWithChannels, BrandChannel, StrategyAsset,
} from '../types';
import { strategyApi } from '../services/strategyApi';

function getStoredChannelId(strategyId: number): number | null {
  try {
    const val = localStorage.getItem(`cs_channel_${strategyId}`);
    return val ? parseInt(val, 10) : null;
  } catch {
    return null;
  }
}

function setStoredChannelId(strategyId: number, channelId: number): void {
  try {
    localStorage.setItem(`cs_channel_${strategyId}`, String(channelId));
  } catch { /* ignore */ }
}

function channelToAccount(ch: BrandChannel): StrategyAccount {
  return {
    username: ch.handle ?? '?',
    followers: ch.followers ?? undefined,
    is_own: true,
    account_id: ch.account_id ?? undefined,
  } as StrategyAccount;
}

export interface StrategyAccountsSlice {
  strategyAccounts: StrategyAccount[];
  strategyPerformance: StrategyPerformance | null;
  strategyFeedback: FeedbackEntry[];
  profileKits: ProfileKit[];
  kitsLoading: boolean;
  accountsLoading: boolean;
  strategyBrand: BrandWithChannels | null;
  selectedChannelId: Record<number, number>;
  resolvedAccount: StrategyAccount | null;
  resolvedChannelId: number | null;

  // Kit v2
  profileKitsV2: ProfileKitV2[];
  kitCounts: KitCounts;
  kitPresets: KitPreset[];
  kitHistory: KitGenerationLog[];
  kitHistoryTotal: number;
  kitGenerating: boolean;
  kitRefining: boolean;
  fieldRefining: string | null;
  promptPreview: { system_prompt: string; user_message: string } | null;

  selectChannel: (strategyId: number, channelId: number) => void;
  fetchStrategyAccounts: (strategyId: number) => Promise<void>;
  assignAccount: (strategyId: number, accountId: number) => Promise<void>;
  unassignAccount: (strategyId: number, accountId: number) => Promise<void>;
  addFeedback: (strategyId: number, type: string, notes?: string) => Promise<void>;
  fetchFeedback: (strategyId: number) => Promise<void>;
  generateKits: (strategyId: number) => Promise<void>;
  fetchKits: (strategyId: number) => Promise<void>;

  // Kit v2 actions
  fetchKitsV2: (strategyId: number, starredOnly?: boolean) => Promise<void>;
  generateKit: (strategyId: number, data: { profile_type: ProfileType; preset_id?: number; custom_rules?: string; model?: string; prompt_override?: string; apply_learned?: boolean; mode?: string }) => Promise<ProfileKitV2 | null>;
  refineKit: (strategyId: number, kitId: number, feedback: string, model?: string) => Promise<ProfileKitV2 | null>;
  refineKitField: (strategyId: number, kitId: number, fieldName: string, feedback: string) => Promise<ProfileKitV2 | null>;
  regenerateKit: (strategyId: number, kitId: number, model?: string) => Promise<ProfileKitV2 | null>;
  updateKit: (strategyId: number, kitId: number, data: Partial<ProfileKitV2>) => Promise<void>;
  deleteKit: (strategyId: number, kitId: number) => Promise<void>;
  toggleStarKit: (strategyId: number, kitId: number, starred: boolean) => Promise<void>;
  previewPrompt: (strategyId: number, profile_type: ProfileType, preset_id?: number, custom_rules?: string) => Promise<void>;
  clearPromptPreview: () => void;
  fetchKitPresets: (strategyId: number) => Promise<void>;
  createKitPreset: (strategyId: number, data: Partial<KitPreset>) => Promise<void>;
  updateKitPreset: (strategyId: number, presetId: number, data: Partial<KitPreset>) => Promise<void>;
  deleteKitPreset: (strategyId: number, presetId: number) => Promise<void>;
  fetchKitHistory: (strategyId: number, limit?: number, offset?: number) => Promise<void>;

  // Strategy Assets
  strategyAssets: StrategyAsset[];
  assetsLoading: boolean;
  fetchStrategyAssets: (strategyId: number, platform?: 'instagram' | 'linkedin') => Promise<void>;
  deleteStrategyAsset: (strategyId: number, assetId: number) => Promise<void>;
}

export const createStrategyAccountsSlice: StateCreator<StrategyAccountsSlice> = (set, get) => ({
  strategyAccounts: [],
  strategyPerformance: null,
  strategyFeedback: [],
  profileKits: [],
  kitsLoading: false,
  accountsLoading: false,
  strategyBrand: null,
  selectedChannelId: {},
  resolvedAccount: null,
  resolvedChannelId: null,

  profileKitsV2: [],
  kitCounts: { today: 0, total: 0 },
  kitPresets: [],
  kitHistory: [],
  kitHistoryTotal: 0,
  kitGenerating: false,
  strategyAssets: [],
  assetsLoading: false,
  kitRefining: false,
  fieldRefining: null,
  promptPreview: null,

  selectChannel: (strategyId, channelId) => {
    setStoredChannelId(strategyId, channelId);
    const brand = get().strategyBrand;
    if (!brand) return;
    const ch = brand.channels.filter(c => c.platform === 'instagram').find(c => c.id === channelId);
    if (!ch) return;
    set({
      resolvedAccount: channelToAccount(ch),
      resolvedChannelId: channelId,
      selectedChannelId: { ...get().selectedChannelId, [strategyId]: channelId },
    });
  },

  fetchStrategyAccounts: async (strategyId) => {
    set({ accountsLoading: true });
    try {
      const { accounts, performance, brand } = await strategyApi.getAccounts(strategyId);
      let resolvedAccount: StrategyAccount | null = null;
      let resolvedChannelId: number | null = null;

      if (brand) {
        const igChannels = brand.channels.filter(c => c.platform === 'instagram');
        if (igChannels.length) {
          const stored = getStoredChannelId(strategyId);
          let selected = stored ? igChannels.find(c => c.id === stored) : undefined;
          if (!selected) {
            selected = igChannels.length === 1
              ? igChannels[0]
              : (igChannels.find(c => c.is_primary === 1) ?? igChannels[0]);
          }
          resolvedAccount = channelToAccount(selected);
          resolvedChannelId = selected.id;
        }
      }

      if (!resolvedAccount) {
        resolvedAccount = accounts[0] ?? null;
      }

      set({
        strategyAccounts: accounts,
        strategyPerformance: performance,
        strategyBrand: brand ?? null,
        resolvedAccount,
        resolvedChannelId,
        accountsLoading: false,
      });
    } catch {
      set({ accountsLoading: false });
    }
  },

  assignAccount: async (strategyId, accountId) => {
    await strategyApi.assignAccount(strategyId, accountId);
    await get().fetchStrategyAccounts(strategyId);
  },

  unassignAccount: async (strategyId, accountId) => {
    await strategyApi.unassignAccount(strategyId, accountId);
    await get().fetchStrategyAccounts(strategyId);
  },

  addFeedback: async (strategyId, type, notes) => {
    await strategyApi.addFeedback(strategyId, { type, notes });
    await get().fetchFeedback(strategyId);
  },

  fetchFeedback: async (strategyId) => {
    const feedback = await strategyApi.getFeedback(strategyId);
    set({ strategyFeedback: feedback });
  },

  generateKits: async (strategyId) => {
    set({ kitsLoading: true });
    try {
      const result = await strategyApi.generateKits(strategyId);
      set({ profileKits: result.kits as ProfileKit[], kitsLoading: false });
    } catch {
      set({ kitsLoading: false });
    }
  },

  fetchKits: async (strategyId) => {
    const kits = await strategyApi.getKits(strategyId);
    set({ profileKits: kits });
  },

  fetchKitsV2: async (strategyId, starredOnly = false) => {
    const { kits, counts } = await strategyApi.getKitsV2(strategyId, starredOnly);
    set({ profileKitsV2: kits, kitCounts: counts });
  },

  generateKit: async (strategyId, data) => {
    set({ kitGenerating: true });
    try {
      const result = await strategyApi.generateKitV2(strategyId, data);
      set((s) => ({
        profileKitsV2: [result.kit, ...s.profileKitsV2],
        kitCounts: { today: s.kitCounts.today + 1, total: s.kitCounts.total + 1 },
        kitGenerating: false,
      }));
      return result.kit;
    } catch {
      set({ kitGenerating: false });
      return null;
    }
  },

  refineKit: async (strategyId, kitId, feedback, model) => {
    set({ kitRefining: true });
    try {
      const result = await strategyApi.refineKit(strategyId, kitId, feedback, model);
      set((s) => ({
        profileKitsV2: [result.kit, ...s.profileKitsV2],
        kitCounts: { today: s.kitCounts.today + 1, total: s.kitCounts.total + 1 },
        kitRefining: false,
      }));
      return result.kit;
    } catch {
      set({ kitRefining: false });
      return null;
    }
  },

  refineKitField: async (strategyId, kitId, fieldName, feedback) => {
    set({ fieldRefining: fieldName });
    try {
      const updated = await strategyApi.refineKitField(strategyId, kitId, fieldName, feedback);
      set((s) => ({
        profileKitsV2: s.profileKitsV2.map((k) => (k.id === kitId ? updated : k)),
        fieldRefining: null,
      }));
      return updated;
    } catch {
      set({ fieldRefining: null });
      return null;
    }
  },

  regenerateKit: async (strategyId, kitId, model) => {
    set({ kitGenerating: true });
    try {
      const result = await strategyApi.regenerateKit(strategyId, kitId, model);
      set((s) => ({
        profileKitsV2: [result.kit, ...s.profileKitsV2],
        kitCounts: { today: s.kitCounts.today + 1, total: s.kitCounts.total + 1 },
        kitGenerating: false,
      }));
      return result.kit;
    } catch {
      set({ kitGenerating: false });
      return null;
    }
  },

  updateKit: async (strategyId, kitId, data) => {
    const updated = await strategyApi.updateKitV2(strategyId, kitId, data);
    set((s) => ({
      profileKitsV2: s.profileKitsV2.map((k) => (k.id === kitId ? updated : k)),
    }));
  },

  deleteKit: async (strategyId, kitId) => {
    await strategyApi.deleteKitV2(strategyId, kitId);
    set((s) => ({ profileKitsV2: s.profileKitsV2.filter((k) => k.id !== kitId) }));
  },

  toggleStarKit: async (strategyId, kitId, starred) => {
    const updated = await strategyApi.updateKitV2(strategyId, kitId, { starred });
    set((s) => ({
      profileKitsV2: s.profileKitsV2.map((k) => (k.id === kitId ? updated : k)),
    }));
  },

  previewPrompt: async (strategyId, profile_type, preset_id, custom_rules) => {
    const result = await strategyApi.previewKitPrompt(strategyId, profile_type, preset_id, custom_rules);
    set({ promptPreview: result });
  },

  clearPromptPreview: () => set({ promptPreview: null }),

  fetchKitPresets: async (strategyId) => {
    const presets = await strategyApi.getKitPresets(strategyId);
    set({ kitPresets: presets });
  },

  createKitPreset: async (strategyId, data) => {
    await strategyApi.createKitPreset(strategyId, data);
    await get().fetchKitPresets(strategyId);
  },

  updateKitPreset: async (strategyId, presetId, data) => {
    await strategyApi.updateKitPreset(strategyId, presetId, data);
    await get().fetchKitPresets(strategyId);
  },

  deleteKitPreset: async (strategyId, presetId) => {
    await strategyApi.deleteKitPreset(strategyId, presetId);
    await get().fetchKitPresets(strategyId);
  },

  fetchKitHistory: async (strategyId, limit = 50, offset = 0) => {
    const result = await strategyApi.getKitHistory(strategyId, limit, offset);
    set({ kitHistory: result.items, kitHistoryTotal: result.total });
  },

  fetchStrategyAssets: async (strategyId, platform) => {
    set({ assetsLoading: true });
    try {
      const assets = await strategyApi.getStrategyAssets(strategyId, platform);
      set({ strategyAssets: assets, assetsLoading: false });
    } catch {
      set({ assetsLoading: false });
    }
  },

  deleteStrategyAsset: async (strategyId, assetId) => {
    await strategyApi.deleteStrategyAsset(strategyId, assetId);
    set((s) => ({ strategyAssets: s.strategyAssets.filter((a) => a.id !== assetId) }));
  },
});
